/**
 * DeepLinkRegistry: Central registry for deep linking logic.
 *
 * Components register their ability to locate DOM elements based on deep link parameters (e.g., video IDs).
 * The registry enables the deep linking conductor to find and trigger actions on specific UI elements,
 * even if they are rendered asynchronously or inside containers like carousels.
 *
 * Usage:
 * - Components call register() on mount to provide a search function for a specific deep link key.
 * - The conductor calls find() to locate the element for a given key and value.
 * - The registry tracks the last found element and can be reset/cleared as needed.
 */
export const DeepLinkRegistry = {
  // The key is the URL parameter (e.g., 'video'),
  // the value is the search function.
  searchers: new Map(),

  // The last found element.
  foundElement: null,

  /**
   * Indicates whether the registry is active.
   * Can be toggled to prevent further registration if URL params do not meet criteria.
   */
  active: true,

  /**
   * Register a search function for a deep link key.
   *
   * @param {string} paramKey - The unique deep link key (e.g., 'deeplink-<videoId>').
   * @param {Object} additionalProps - Object containing:
   *   - searchFunction: (value: string) => HTMLElement | null
   *       Function that returns the DOM element for the given value.
   *   - isCarouselSlide: boolean (optional)
   *       Whether the element is inside a carousel slide.
   */
  register(paramKey, additionalProps) {
    this.searchers.set(paramKey, additionalProps);
  },

  /**
   * Attempts to find an element using the registered searchers.
   *
   * @param {string} paramKey - The deep link key (e.g., 'deeplink-<videoId>').
   * @param {string} value - The value from the URL parameter.
   * @returns {Object|null} - { element: HTMLElement|null, isCarouselSlide: boolean } or null if not found.
   */
  find(paramKey, value) {
    if (this.searchers.has(paramKey)) {
      const { searchFunction, isCarouselSlide } = this.searchers.get(paramKey);
      return {
        element: searchFunction(value),
        isCarouselSlide: isCarouselSlide || false,
      };
    }
    return null;
  },

  /**
   * Checks if the registry is active and the paramKey is not already registered.
   *
   * @param {string} paramKey - The deep link key to check.
   * @returns {boolean} - True if active and not registered, false otherwise.
   */
  isActiveAndNotRegistered(paramKey) {
    return this.active && !this.searchers.has(paramKey);
  },

  /**
   * Sets the last found element and its context.
   *
   * @param {Object} element - Object containing:
   *   - element: HTMLElement (the found element)
   *   - isCarouselSlide: boolean (optional)
   */
  setFoundElement(element) {
    this.foundElement = element;
  },

  /**
   * Gets the last found element and its context.
   *
   * @returns {Object|null} - The last found element object or null.
   */
  getFoundElement() {
    return this.foundElement;
  },

  /**
   * Clears all registered searchers and resets the registry.
   * Useful for testing or resetting state between navigations.
   */
  clear() {
    this.searchers.clear();
  },
};
